import _ from 'lodash';
import React, { useState } from 'react';
import { Field } from 'react-final-form';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Search from '@mui/icons-material/Search';
import { Button, FieldError } from '@/ui';
import { useContainersTable, useLayoutsTable } from '../queries';

import './RightAside.css';

const LABEL_SX = { display: 'block', mb: 0.5 };

const openInNewTab = (url) => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) newWindow.opener = null;
};

/**
 * Panes stay mounted and are hidden, the way reactstrap's TabPane did. These hold
 * react-final-form Fields, and a Field that unmounts unregisters itself — taking its value and
 * its validation with it.
 */
const TabPanel = ({ value, tabId, children }) => (
  <Box role='tabpanel' hidden={value !== tabId}>
    {children}
  </Box>
);

/** A read-only value shown in a field-shaped box, as the hardcoded bordered divs did. */
const ReadOnlyValue = ({ label, children }) => (
  <>
    <FormLabel sx={LABEL_SX}>{label}</FormLabel>
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 0.75 }}>{children}</Box>
  </>
);

const resolveValue = (value, data) => {
  if (value === '' || value === null || value === undefined) return null;
  if (typeof value === 'object') return value;
  return (data ?? []).find((option) => option?.id === value) ?? value;
};

const optionLabel = (option) =>
  option && typeof option === 'object' ? option.name ?? '' : String(option ?? '');

/**
 * react-final-form's input against an MUI Autocomplete, matching the combo renderers.
 *
 * The form stores these as a bare id until something is picked. Combobox resolved that through
 * `dataKey` and showed the matching name; MUI has no equivalent, so an unresolved id has to be
 * looked up here or the field displays the number.
 */
const PickerField = ({ name, label, data, loading, disabled }) => (
  <>
    <FormLabel htmlFor={name} sx={LABEL_SX}>
      {label}
    </FormLabel>
    <Field name={name}>
      {({ input, meta: { touched, error } }) => (
        <>
          <Autocomplete
            freeSolo
            forcePopupIcon
            autoHighlight
            id={name}
            options={data ?? []}
            value={resolveValue(input.value, data)}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(option, value) => (option?.id ?? option) === (value?.id ?? value)}
            loading={loading}
            disabled={disabled}
            onChange={(_event, selected) => input.onChange(selected === null ? '' : selected)}
            onInputChange={(_event, text, reason) => {
              if (reason === 'input') input.onChange(text);
            }}
            onBlur={() => input.onBlur()}
            renderInput={(params) => (
              <TextField
                {...params}
                name={input.name}
                error={Boolean(touched && error)}
                slotProps={{
                  ...params.slotProps,
                  input: {
                    ...params.slotProps.input,
                    endAdornment: (
                      <>
                        {loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                        {params.slotProps.input.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
          <FieldError error={error} touched={touched} />
        </>
      )}
    </Field>
  </>
);

const RightAside = (props) => {
  const [activeTab, setActiveTab] = useState('offerBlock');
  const [activeOfferSubsectionTab, setActiveOfferSubsectionTab] = useState('properties');
  const [activeSequenceSubSectionTab, setActiveSequenceSubSectionTab] = useState('properties');
  const {
    data: layouts,
    isLoading: isLayoutsLoading,
    isError: isLayoutsError,
  } = useLayoutsTable((data) => _.orderBy(data, ['name'], ['asc']));
  const {
    data: containers,
    isLoading: isContainersLoading,
    isError: isContainersError,
  } = useContainersTable((data) => _.orderBy(data, ['name'], ['asc']));

  const filteredContainers = containers?.filter(
    (container) => props.selectedElement?.offerType?.id === container?.offerTypeHint
  );

  const renderOfferMetaData = () => {
    if (!props.selectedElement.offer) {
      return <></>;
    }
    const offerId = props.selectedElement?.offer?.id;
    return (
      <Stack spacing={2} sx={{ mt: 2 }}>
        <Stack direction='row' spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant='h5' component='h3'>
            {props.selectedElement?.offer?.name}
          </Typography>
          <Tooltip describeChild title={`Open Offer ${offerId} in a new tab`}>
            <Button
              color='info'
              aria-label={`Open Offer ${offerId} in a new tab`}
              sx={{ minWidth: 0, px: 1 }}
              onClick={() => openInNewTab(`/campaigns/offertable/offer/${offerId}`)}
            >
              <Search fontSize='small' />
            </Button>
          </Tooltip>
        </Stack>
        <Grid container spacing={2}>
          <Grid size={6}>
            <Typography variant='subtitle2'>
              <em>Offer ID: </em>
              {offerId}
            </Typography>
          </Grid>
          <Grid size={6}>
            <Field required name={`offerBlockOffers[${props.selectedElementIndex}].id`}>
              {({ input }) => (
                <Typography variant='subtitle2'>
                  <em>Offer Block ID: </em>
                  {input.value}
                </Typography>
              )}
            </Field>
          </Grid>
          <Grid size={6}>
            <ReadOnlyValue label='Offer Type'>{props.selectedElement.offer?.offerType.name}</ReadOnlyValue>
          </Grid>
          <Grid size={6}>
            <ReadOnlyValue label='Advertiser'>{props.selectedElement.offer?.advertiser?.name}</ReadOnlyValue>
          </Grid>
        </Grid>
        <PickerField
          name={`offersPublished.offer_block[${props.selectedElementIndex}].layout_id`}
          label='Layout'
          data={layouts}
          loading={isLayoutsLoading}
          disabled={isLayoutsError || isLayoutsLoading}
        />
        <PickerField
          name={`offersPublished.offer_block[${props.selectedElementIndex}].container_id`}
          label='Container'
          data={filteredContainers}
          loading={isContainersLoading}
          disabled={isContainersError || isContainersLoading}
        />
      </Stack>
    );
  };

  return (
    <div className='right-aside-container'>
      <Tabs value={activeTab} onChange={(_event, next) => setActiveTab(next)} variant='fullWidth'>
        <Tab value='offer' label='Offer' />
        <Tab value='offerBlock' label='Offer Block' />
      </Tabs>
      <TabPanel value={activeTab} tabId='offer'>
        <Tabs
          value={activeOfferSubsectionTab}
          onChange={(_event, next) => setActiveOfferSubsectionTab(next)}
          variant='fullWidth'
        >
          <Tab value='properties' label='Properties' />
        </Tabs>
        <Box id='offerSubSection'>
          <TabPanel value={activeOfferSubsectionTab} tabId='properties'>
            {renderOfferMetaData()}
          </TabPanel>
        </Box>
      </TabPanel>
      <TabPanel value={activeTab} tabId='offerBlock'>
        <Tabs
          value={activeSequenceSubSectionTab}
          onChange={(_event, next) => setActiveSequenceSubSectionTab(next)}
          variant='fullWidth'
        >
          <Tab value='properties' label='Properties' />
        </Tabs>
        <Box id='sequenceSubSection'>
          <TabPanel value={activeSequenceSubSectionTab} tabId='properties'>
            {/* The Active toggle moved up into the page header, where every detail page keeps it. */}
            <Typography component='span' sx={{ display: 'block', mt: 2 }}>
              Properties
            </Typography>
          </TabPanel>
        </Box>
      </TabPanel>
    </div>
  );
};
export default RightAside;
